import getConversationId from './getConversationId';
import isGroupItem from './isGroupItem';
import isInJunkFolder from './isInJunkFolder';
import { ItemActionsProps } from './schema/ItemActionsProps';
import shouldAddAssignPolicyMenuItem from './shouldAddAssignPolicyMenuItem';
import shouldAddBlockSafeSenderMenuItemForFolder from './shouldAddBlockSafeSenderMenuItemForFolder';
import shouldDisableTranslate from './shouldDisableTranslate';
import CustomLikeButton from '../components/CustomLikeButton';
import onAddToSafeSenders from '../handlers/onAddToSafeSenders';
import onBlock from '../handlers/onBlock';
import onCreateRule from '../handlers/onCreateRule';
import onCustomizeActions from '../handlers/onCustomizeActions';
import onDelete from '../handlers/onDelete';
import onForward from '../handlers/onForward';
import onLikeUnlike from '../handlers/onLikeUnlike';
import onPhishing from '../handlers/onPhishing';
import onPopout from '../handlers/onPopout';
import onPrint from '../handlers/onPrint';
import onReply from '../handlers/onReply';
import onReplyAll from '../handlers/onReplyAll';
import onReplyAllByIm from '../handlers/onReplyAllByIm';
import onReplyByIm from '../handlers/onReplyByIm';
import onReplyByMeeting from '../handlers/onReplyByMeeting';
import onReportAbuse from '../handlers/onReportAbuse';
import onShowInImmersiveReader from '../handlers/onShowInImmersiveReader';
import onToggleAmp from '../handlers/onToggleAmp';
import onToggleFlagMenuItem from '../handlers/onToggleFlagMenuItem';
import onToggleJunkNotJunk from '../handlers/onToggleJunkNotJunk';
import onToggleReadMenuItem from '../handlers/onToggleReadMenuItem';
import onToggleUndoDarkMode from '../handlers/onToggleUndoDarkMode';
import onTranslate from '../handlers/onTranslate';
import onViewMessageSource from '../handlers/onViewMessageSource';
import getSenderNameForBlockMenuItem from '../utils/getSenderNameForBlockMenuItem';
import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isFolderInMailboxType, isFolderOrSubFolderOfMsgRoot } from 'owa-folders';
import loc, { format } from 'owa-localize';
import { itemHeaderBlock } from 'owa-locstrings/lib/strings/itemheaderblock.locstring.json';
import { itemHeaderFlag } from 'owa-locstrings/lib/strings/itemheaderflag.locstring.json';
import { itemHeaderLightsOn } from 'owa-locstrings/lib/strings/itemheaderlightson.locstring.json';
import { itemHeaderMarkAsJunk } from 'owa-locstrings/lib/strings/itemheadermarkasjunk.locstring.json';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import { viewAsDynamic } from 'owa-locstrings/lib/strings/viewasdynamic.locstring.json';
import { viewAsOriginal } from 'owa-locstrings/lib/strings/viewasoriginal.locstring.json';
import { AMPAvailability } from 'owa-mail-amp-store/lib/store/schema/AmpViewState';
import isAmpEnabled from 'owa-mail-amp-store/lib/utils/isAmpEnabled';
import { MailIcons } from 'owa-mail-icons';
import isInboxRuleEnabledByAdmin from 'owa-mail-inbox-rules/lib/selectors/isInboxRuleEnabledByAdmin';
import { getSelectedTableView, isItemOfMessageType } from 'owa-mail-list-store';
import { ReactionActionButton } from 'owa-mail-reactions-view';
import checkItemReplyForwardDisabled from 'owa-mail-reading-pane-item-actions/lib/utils/checkItemReplyForwardDisabled';
import { lazyGetAssignPolicyPropertiesForMailContextMenu } from 'owa-mail-retention-policies';
import getItemRightsManagementRestrictions from 'owa-mail-store/lib/utils/getItemRightsManagementRestrictions';
import { ReadActionKey } from 'owa-outlook-service-options';
import Message from 'owa-service/lib/contract/Message';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import doesFolderIdEqualName from 'owa-session-store/lib/utils/doesFolderIdEqualName';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { isReplyByImEnabled } from 'owa-skype-for-business';
import isSMIMEItem from 'owa-smime/lib/utils/isSMIMEItem';
import { shouldActionBeOnReadingPaneSurface, SurfaceActionItem } from 'owa-surface-actions';
import { lazyGetSpacesContextMenuItems } from 'owa-timestream-contextmenupicker';
import * as React from 'react';
import {
    isJunkEmailEnabledByAdmin,
    doesUserHaveSharedFolderPermissionFor,
    MenuItemType,
} from 'owa-mail-filterable-menu-behavior';
import {
    itemHeaderLightsOff,
    itemHeaderUnflag,
    itemHeaderUnlike,
    itemHeaderMarkAsNotJunk,
} from '../components/ItemActions.locstring.json';

export const updateMenuItem: Partial<
    Record<ReadActionKey, (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => void>
> = {
    ['Reply']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const replyForwardRestrictions = checkItemReplyForwardDisabled(
            props.item,
            props.viewState.isConversationItemPart
        );
        commandBarItem.disabled = replyForwardRestrictions[0] /* isReplyDisabled */;
        commandBarItem.onClick = onReply(
            props.instrumentationContext,
            props.viewState,
            props.item,
            props.targetWindow
        );
    },

    ['ReplyAll']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const replyForwardRestrictions = checkItemReplyForwardDisabled(
            props.item,
            props.viewState.isConversationItemPart
        );
        commandBarItem.disabled = replyForwardRestrictions[1] /* isReplyAllDisabled */;
        commandBarItem.onClick = onReplyAll(
            props.instrumentationContext,
            props.viewState,
            props.item,
            props.targetWindow
        );
    },

    ['Forward']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const replyForwardRestrictions = checkItemReplyForwardDisabled(
            props.item,
            props.viewState.isConversationItemPart
        );
        commandBarItem.disabled = replyForwardRestrictions[2] /* isForwardDisabled */;
        commandBarItem.onClick = onForward(
            props.item,
            props.instrumentationContext,
            props.targetWindow
        );
    },

    ['ReplyByMeeting']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.disabled =
            isSMIMEItem(props.item) || !!props.item.RightsManagementLicenseData;
        commandBarItem.onClick = onReplyByMeeting(props.item);
    },

    ['OtherReplyActions']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = stopEventPropagation;
        commandBarItem.subMenuActionKeys = [
            'ReplyByMeeting',
            'ReplyByIM',
            'ReplyAllByIM',
            'LikeUnlike',
        ];
    },

    ['MessageSafety']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = stopEventPropagation;
        commandBarItem.subMenuActionKeys = [
            'MarkJunkNotJunk',
            'MarkAsPhishing',
            'Block',
            'ReportAbuse',
        ];
    },

    ['ReplyByIM']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = onReplyByIm(props.item as Message);
        commandBarItem.disabled = !isReplyByImEnabled();
    },

    ['ReplyAllByIM']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = onReplyAllByIm(props.item as Message);
        commandBarItem.disabled = !isReplyByImEnabled();
    },

    ['MarkJunkNotJunk']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const message = props.item as Message;
        if (
            isJunkEmailEnabledByAdmin() &&
            isFolderOrSubFolderOfMsgRoot(message.ParentFolderId.Id)
        ) {
            // Override default string based on actual state
            const displayName = isInJunkFolder(message)
                ? loc(itemHeaderMarkAsNotJunk)
                : loc(itemHeaderMarkAsJunk);

            commandBarItem.text = displayName;
            commandBarItem.ariaLabel = displayName;
            commandBarItem.datapointName = isInJunkFolder(message)
                ? 'MarkItemNotjunk'
                : 'MarkItemJunk';
            commandBarItem.onClick = onToggleJunkNotJunk(props.item, props.instrumentationContext);
        }
    },

    ['Delete']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        if (!props.isItemAttachment && doesUserHaveSharedFolderPermissionFor(MenuItemType.Delete)) {
            const message: Message = props.item;
            commandBarItem.onClick = onDelete(
                props.viewState,
                props.item,
                props.instrumentationContext
            );
            commandBarItem.disabled =
                isGroupItem(props.item) &&
                !message.CanDelete; /* Use canDelete property for group messages */
        }
    },

    ['MarkReadUnread']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        /* Temporarily disable for groups popouts */
        if (!(props.isPopout && isGroupItem(props.item))) {
            const message: Message = props.item;
            const displayName = message.IsRead ? loc(markAsUnread) : loc(markAsRead);
            commandBarItem.text = displayName;
            commandBarItem.ariaLabel = displayName;
            commandBarItem.datapointName = message.IsRead ? 'MarkItemUnread' : 'MarkItemRead';
            if (!message.IsRead) {
                commandBarItem.iconProps.iconName = ControlIcons.Read;
            }
            commandBarItem.onClick = onToggleReadMenuItem(props.item, props.instrumentationContext);
        }
    },

    ['FlagUnflag']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        if (doesUserHaveSharedFolderPermissionFor(MenuItemType.Flag)) {
            const message: Message = props.item;
            const isUnflag = !message.Flag || message.Flag.FlagStatus != 'Flagged';
            const displayName = isUnflag ? loc(itemHeaderFlag) : loc(itemHeaderUnflag);
            commandBarItem.text = displayName;
            commandBarItem.ariaLabel = displayName;
            commandBarItem.datapointName = isUnflag ? 'FlagItem' : 'UnflagItem';
            commandBarItem.onClick = onToggleFlagMenuItem(props.item, props.instrumentationContext);
        }
    },

    ['Print']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const IRMRestrictions = getItemRightsManagementRestrictions(props.item);
        commandBarItem.onClick = onPrint(props.item);
        commandBarItem.disabled = !IRMRestrictions.PrintAllowed;
    },

    ['View']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = stopEventPropagation;
        commandBarItem.subMenuActionKeys = [
            'Popout',
            'Translate',
            'ShowInImmersiveReader',
            'ViewMessageSource',
        ];
    },

    ['AdvancedActions']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = stopEventPropagation;
        commandBarItem.subMenuActionKeys = ['CreateRule', 'AssignPolicy'];
    },

    ['Popout']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = onPopout(props.item, props.viewState);
    },

    ['CreateRule']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const message = isItemOfMessageType(props.item) ? (props.item as Message) : null;
        if (
            isInboxRuleEnabledByAdmin() &&
            !!message?.From?.Mailbox &&
            isFolderOrSubFolderOfMsgRoot(
                message?.ParentFolderId.Id
            ) /* Checking if the item is from primary mailbox */
        ) {
            commandBarItem.onClick = onCreateRule(props.item as Message, props.actionSource);
        }
    },

    ['AddToSafeSenders']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const message = props.item as Message;
        if (shouldAddBlockSafeSenderMenuItemForFolder(message)) {
            commandBarItem.onClick = onAddToSafeSenders(message, props.actionSource);
        }
    },

    ['MarkAsPhishing']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = onPhishing(props.item);
    },

    ['Block']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const message = props.item as Message;
        if (
            shouldAddBlockSafeSenderMenuItemForFolder(message) &&
            isFolderOrSubFolderOfMsgRoot(
                message.ParentFolderId.Id
            ) /* Checking if the item is from primary mailbox */
        ) {
            const displayName = format(
                loc(itemHeaderBlock),
                getSenderNameForBlockMenuItem(message)
            );
            commandBarItem.text = displayName;
            commandBarItem.ariaLabel = displayName;
            commandBarItem.onClick = onBlock(props.item, props.instrumentationContext);
        }
    },

    ['ReportAbuse']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        if (isFeatureEnabled('tri-reportAbuse')) {
            commandBarItem.onClick = onReportAbuse(props.item, props.instrumentationContext);
        }
    },

    ['Translate']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const IRMRestrictions = getItemRightsManagementRestrictions(props.item);
        commandBarItem.onClick = onTranslate(props.item, props.viewState);
        commandBarItem.disabled = shouldDisableTranslate(IRMRestrictions, props.item);
    },

    ['ShowInImmersiveReader']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = onShowInImmersiveReader(props.item, props.actionSource);
    },

    ['ViewMessageSource']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const message: Message = props.item;

        // Do not show if user is viewing own sent item in sent folder,
        // because message details are not stamped in this case.
        const isSentItem = doesFolderIdEqualName(message.ParentFolderId.Id, 'sentitems');
        if (!isSentItem) {
            commandBarItem.onClick = onViewMessageSource(props.item, props.actionSource);
        }
    },

    ['AssignPolicy']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const message: Message = props.item;

        if (shouldAddAssignPolicyMenuItem(message.ParentFolderId.Id, props.item)) {
            const selectedTableView = getSelectedTableView();
            const getAssignPolicyMenu = lazyGetAssignPolicyPropertiesForMailContextMenu.tryImportForRender();
            let submenuProps;
            if (getAssignPolicyMenu) {
                submenuProps = getAssignPolicyMenu(
                    selectedTableView?.id,
                    'RPCSAOverflow',
                    props.item
                );
            }

            commandBarItem.onClick = stopEventPropagation;
            commandBarItem.subMenuProps = submenuProps as IContextualMenuProps;
        }
    },

    ['ToggleDarkMode']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const undoDarkMode = props.viewState.undoDarkMode;
        const displayName = undoDarkMode ? loc(itemHeaderLightsOff) : loc(itemHeaderLightsOn);
        commandBarItem.text = displayName;
        commandBarItem.ariaLabel = displayName;
        commandBarItem.iconProps.iconName = undoDarkMode
            ? ControlIcons.ClearNight
            : ControlIcons.Sunny;
        commandBarItem.datapointName = undoDarkMode ? 'ToggleDarkModeOn' : 'ToggleDarkModeOff';
        commandBarItem.onClick = onToggleUndoDarkMode(props.viewState);
        commandBarItem.isPinnedOnly = true;
    },

    ['SetReaction']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const message: Message = props.item;
        if (
            !isFolderInMailboxType(message.ParentFolderId.Id, 'SharedMailbox') &&
            !isGroupItem(props.item)
        ) {
            commandBarItem.onClick = stopEventPropagation;
            commandBarItem.isPinnedOnly = true;
            // Set a custom render if present on the surface
            if (shouldActionBeOnReadingPaneSurface('SetReaction', message)) {
                commandBarItem.onRender = (surfaceItem: SurfaceActionItem) => {
                    return (
                        <ReactionActionButton
                            commandBarItem={surfaceItem}
                            conversationId={{
                                Id: getConversationId(props.viewState, props.item),
                                mailboxInfo: props.item.MailboxInfo,
                            }}
                            itemId={{
                                ...props.item.ItemId,
                                mailboxInfo: props.item.MailboxInfo,
                            }}
                            item={props.item}
                            instrumentationContext={props.instrumentationContext}
                        />
                    );
                };
            }
        }
    },

    ['Addins']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = stopEventPropagation;
    },

    ['AddToProjectSpace']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const spacesSubMenuProps = lazyGetSpacesContextMenuItems.tryImportForRender();
        if (spacesSubMenuProps) {
            commandBarItem.onClick = stopEventPropagation;
            commandBarItem.subMenuProps = spacesSubMenuProps(
                props.item.ItemId.Id,
                props.item,
                'MailModuleNotificationBarHost'
            );
        }
    },

    ['-']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        commandBarItem.onClick = stopEventPropagation;
    },

    ['LikeUnlike']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const message: Message = props.item;

        if (!isConsumer() && !isFolderInMailboxType(message.ParentFolderId.Id, 'SharedMailbox')) {
            commandBarItem.onClick = onLikeUnlike(
                props.item,
                props.instrumentationContext,
                props.viewState
            );

            if (hasUserLikedMessage(message)) {
                commandBarItem.iconProps.iconName = MailIcons.LikeSolid;
                const displayName = loc(itemHeaderUnlike);
                commandBarItem.text = displayName;
                commandBarItem.ariaLabel = displayName;
            }

            // Set a custom render if present on the surface
            if (shouldActionBeOnReadingPaneSurface('LikeUnlike', message)) {
                commandBarItem.onRender = (item: SurfaceActionItem) => {
                    return <CustomLikeButton surfaceActionItem={item} message={message} />;
                };
            }
        }
    },

    ['ToggleAmp']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        const { preferAmp, ampAvailability } = props.viewState.ampViewState;
        if (isAmpEnabled() && ampAvailability == AMPAvailability.AmpValidated) {
            const displayName = preferAmp ? loc(viewAsOriginal) : loc(viewAsDynamic);
            commandBarItem.text = displayName;
            commandBarItem.ariaLabel = displayName;
            commandBarItem.datapointName = preferAmp ? 'ViewAsOriginal' : 'ViewAsDynamic';
            commandBarItem.onClick = onToggleAmp(props.viewState);
            commandBarItem.isPinnedOnly = true;
            if (preferAmp) {
                commandBarItem.iconProps.iconName = MailIcons.LightningBoltSolid;
            }
        }
    },

    ['CustomizeActions']: (props: ItemActionsProps, commandBarItem: SurfaceActionItem) => {
        if (isFeatureEnabled('addin-exp-customizedActionsButtonNavBar')) {
            commandBarItem.onClick = onCustomizeActions();
        }
    },
};

function stopEventPropagation(
    event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>
) {
    event.stopPropagation();
}

function hasUserLikedMessage(message: Message): boolean {
    const userEmailAddress = getUserConfiguration().SessionSettings.UserEmailAddress;
    return message.Likers
        ? message.Likers.some(email => email.EmailAddress === userEmailAddress)
        : false;
}
